// Passive target ring for the printed inductive encoder: the copper-lobe disc
// that glues into the rotor (behind the magnet stack) and modulates the
// sensor's TX field as it rotates past the fine/coarse RX bands.
//
// Pure TypeScript. No Angular / DI imports. Deterministic (no Date/random).
// Angles are radians; math frame is y-up. This module NEVER derives its own
// track radii — it consumes `EncoderBands` from pcb-stator-encoder-bands so
// the target's copper and the sensor's coils are structurally locked to the
// same [rIn, rOut] per track (misalignment there is a silently-dead sensor).
//
// Construction (normative, from the task brief): for a track with periodicity
// `p`, copper occupies HALF of each electrical period (2*pi/p rad) — a
// gear-tooth pattern of `p` lobes with `p` equal gaps — so consecutive lobes
// modulate the RX sin/cos windings into a clean position signal. Lobe 0 is
// centered on `datumRad` (pole-0 datum, matches the rotor's index notch and
// the coil module's `datumRad`), so it straddles the datum symmetrically.
// Lobes fill their band radially exactly (no trace-width inset — this is
// solid copper, not a winding), which is what makes the cross-module
// band-identity check exact rather than approximate.
//
// The disc outline is the OUTER RX track's copper envelope plus a 1 mm rim
// (`bands.fine.rOutMm + 1`; 2026-07-13 seating fix), plus a small radial key tab
// at `keyAzimuthRad(datumRad, periods.fine)` — half a pole pitch off the datum,
// the middle of the solid web between magnet pockets — that mates with the
// rotor's key slot so the target can only glue in at the correct phase
// (2026-07-10 refinement: a tab ON the datum sits over pocket 0, where the rotor
// has no material for the mating finger).
//
// WHY fine.rOutMm, NOT tx.rOutMm (2026-07-13): the disc is a PASSIVE eddy-current
// target — it modulates the coupling the sensor board's TX loop induces into the
// RX coils, and that modulation happens under the RX lobe tracks (fine + coarse).
// The TX excitation loop needs a field over the target, NOT target substrate under
// its own copper. Ruling the disc OD off tx.rOutMm (the OLD rule) oversized the
// disc by the whole TX wrap (R29.6 vs the pocket-clamped recess wall at R25.9), so
// it rested proud of the rotor counterbore and the sensing air gap collapsed. The
// disc only needs to cover the fine RX lobes (+ rim), so its OD is fine.rOutMm + rim
// — and the bands module (pcb-stator-encoder-bands) shaves fine.rOutMm just enough
// that fine.rOut + rim + fit ≤ the recess wall, so the disc seats FULLY.

import { Band, EncoderBands } from './pcb-stator-encoder-bands';

export interface TargetLobe { track: 'fine' | 'coarse'; polygon: Array<[number, number]>; }
export interface TargetRing {
  lobes: TargetLobe[];               // copper regions, up-face
  outline: Array<[number, number]>;  // disc edge incl. the key tab
  boreRadiusMm: number;
  keyTabAngleRad: number;            // = keyAzimuthRad(datumRad, periods.fine); matches rotor key slot
  thicknessMm: number;               // default 0.8
}

export const KEY_TAB_WIDTH_MM = 3;
export const KEY_TAB_DEPTH_MM = 2;   // radial protrusion beyond disc edge
export const DISC_RIM_MM = 1;        // disc OD = bands.fine.rOutMm + DISC_RIM_MM (2026-07-13 seating fix; was tx.rOutMm)

/** THE single source of the key azimuth (2026-07-10 refinement): the tab sits
 *  half a pole pitch off the pole-0 datum — the middle of the solid web
 *  between adjacent magnet pockets (numPoles = 2 * periods.fine, so the pole
 *  pitch is pi/finePeriods). On the datum itself the rotor's mating key
 *  finger would land on top of pocket 0 and clamp away, silently losing the
 *  "cannot glue in rotated" keying guarantee. Both the target tab and the
 *  rotor's recess key finger MUST derive their angle from this helper. */
export function keyAzimuthRad(datumRad: number, finePeriods: number): number {
  return datumRad + Math.PI / (2 * finePeriods);
}

const SECTOR_ARC_SAMPLES = 16;   // per-arc vertex count for lobe polygons (cosmetic smoothness)
const OUTLINE_ARC_SAMPLES = 128; // vertex count for the disc's main circular arc (excl. tab)

type Pt = [number, number];

/** An annular-sector polygon on `band`, sweeping [angleStart, angleEnd]:
 *  outer arc forward, then inner arc backward, closing the loop. Vertices sit
 *  exactly at band.rInMm / band.rOutMm (no inset), so the polygon's radial
 *  extent equals the band exactly. */
function sectorPolygon(band: Band, angleStart: number, angleEnd: number): Pt[] {
  const pts: Pt[] = [];
  for (let i = 0; i <= SECTOR_ARC_SAMPLES; i++) {
    const a = angleStart + (angleEnd - angleStart) * (i / SECTOR_ARC_SAMPLES);
    pts.push([band.rOutMm * Math.cos(a), band.rOutMm * Math.sin(a)]);
  }
  for (let i = SECTOR_ARC_SAMPLES; i >= 0; i--) {
    const a = angleStart + (angleEnd - angleStart) * (i / SECTOR_ARC_SAMPLES);
    pts.push([band.rInMm * Math.cos(a), band.rInMm * Math.sin(a)]);
  }
  return pts;
}

/** `period` lobes on `band`, each spanning half an electrical period
 *  (pi/period rad) with an equal gap (the other half) between them. Lobe k's
 *  center is `datumRad + k * (2*pi/period)`, so lobe 0 straddles the datum. */
function buildTrackLobes(band: Band, period: number, datumRad: number, track: 'fine' | 'coarse'): TargetLobe[] {
  const halfSpan = Math.PI / (2 * period);
  const fullPeriod = (2 * Math.PI) / period;
  const lobes: TargetLobe[] = [];
  for (let k = 0; k < period; k++) {
    const center = datumRad + k * fullPeriod;
    lobes.push({ track, polygon: sectorPolygon(band, center - halfSpan, center + halfSpan) });
  }
  return lobes;
}

/** The disc's outer edge: a circle of `discRadiusMm` with a small radial key
 *  tab cut in at `keyTabAngleRad`. The tab's base corners sit exactly on the
 *  base circle (angle offset by asin(halfWidth/radius)) so there is no seam
 *  between the arc and the tab; its tip corners sit at the same angles,
 *  radius `discRadiusMm + KEY_TAB_DEPTH_MM`. The loop is closed explicitly
 *  (first vertex repeated as the last). */
function buildOutline(discRadiusMm: number, keyTabAngleRad: number): Pt[] {
  const angHalf = Math.asin((KEY_TAB_WIDTH_MM / 2) / discRadiusMm);
  const tipR = discRadiusMm + KEY_TAB_DEPTH_MM;
  const tabRightA = keyTabAngleRad + angHalf;
  const tabLeftA = keyTabAngleRad - angHalf;

  const startA = tabRightA;
  const endA = tabLeftA + 2 * Math.PI;   // long way around, the tab's base arc omitted

  const pts: Pt[] = [];
  for (let i = 0; i <= OUTLINE_ARC_SAMPLES; i++) {
    const a = startA + (endA - startA) * (i / OUTLINE_ARC_SAMPLES);
    pts.push([discRadiusMm * Math.cos(a), discRadiusMm * Math.sin(a)]);
  }
  // The base arc ends at tabLeftA, so the tab walk must continue LEFT tip
  // first, then RIGHT tip, then close back to the base corner at tabRightA
  // (pts[0]): base@left -> tip@left -> tip@right -> base@right, a simple
  // trapezoid (the tip is a 2-vertex chord, not an arc). Pushing the right
  // tip first crossed the two diagonals into a self-intersecting bowtie
  // (caught by the polygon-simplicity spec).
  pts.push([tipR * Math.cos(tabLeftA), tipR * Math.sin(tabLeftA)]);
  pts.push([tipR * Math.cos(tabRightA), tipR * Math.sin(tabRightA)]);
  pts.push(pts[0]);   // explicit closure
  return pts;
}

/** Builds the complete passive target ring: fine + coarse copper lobes (the
 *  modulation pattern) and the disc outline (edge + key tab). */
export function buildTargetRing(
  bands: EncoderBands,
  periods: { fine: number; coarse: number },
  datumRad: number,
  boreRadiusMm: number,
  thicknessMm: number,
): TargetRing {
  const fineLobes = buildTrackLobes(bands.fine, periods.fine, datumRad, 'fine');
  const coarseLobes = buildTrackLobes(bands.coarse, periods.coarse, datumRad, 'coarse');
  const discRadiusMm = bands.fine.rOutMm + DISC_RIM_MM;
  const keyTabAngleRad = keyAzimuthRad(datumRad, periods.fine);
  const outline = buildOutline(discRadiusMm, keyTabAngleRad);

  return {
    lobes: [...fineLobes, ...coarseLobes],
    outline,
    boreRadiusMm,
    keyTabAngleRad,
    thicknessMm,
  };
}
